import { useLazyQuery } from '@apollo/client';
import { Alert, Button, Typography } from '@mui/material';
import { isEmpty } from 'lodash';
import React, { Dispatch, SetStateAction, useContext, useEffect, useState } from 'react';
import type { Reference } from 'sr-types/lib/production/v1/graphql';
import { H, V } from '../common/Layout';
import { useStyling } from '../common/Theme';
import { UserContext } from '../common/auth/UserContext';
import { CONSTANTS_ID, FETCH_POLICY_CONFIG } from '../common/constants/constants';
import { FormContainer } from '../common/form/FormContainer';
import { FormContext } from '../common/form/FormContext';
import FormItem from '../common/form/FormItem';
import FormPanel from '../common/form/FormPanel';
import FormWidget from '../common/form/FormWidget';
import { FORM_WIDGET_COMPONENTS } from '../common/form/formConstants';
import { useMutationWithContext } from '../common/hooks/useMutationWithContext';
import I18n from '../common/i18n/I18n';
import { searchClient, slimResultsQuery } from '../common/list/slimQuery';
import useHistory from '../common/utils/useHistory';
import ParentOrganizationRenderer from '../organization/OrganizationOptionRenderer';
import { getOption, quickAddOrganization } from '../organization/QuickAddOrganization';
import { autoCreateQuery, client } from '../organization/organization';
import { getOption as getProductionOption, quickAddProduction } from '../production/helpers/QuickAddProduction';
import {
    autoCreateQuery as autoCreateProductionQuery,
    client as productionClient
} from '../production/helpers/production';
import { RESOURCE_TYPE } from '../resource/common/resourceConstants';
import { ProductionOptionRenderer } from '../supply/booking/ProductionOptionRenderer';

const EMPTY_CONTACT = {
    Name: '',
    Organization: '',
    Role: '',
    Production: '',
    ProductionRole: ''
};

export type DisabledOrDeletedFieldsType = {
    creativeCredits?: { department?: boolean; showReference?: boolean };
    productionRole?: boolean;
    organizationRole?: boolean;
    experiences?: { deparment?: boolean; title?: boolean; organizationReference?: boolean };
    contactInfo?: { phone?: boolean; email?: boolean };
};

interface GuestContactInfoProps {
    pageSize?: number;
    isInModal?: boolean;
    setIsSaveDisabled?: Dispatch<SetStateAction<boolean>>;
    productionRef?: Reference;
    hiddenFields?: DisabledOrDeletedFieldsType;
    disabledFields?: DisabledOrDeletedFieldsType;
    showTypeFields?: boolean;
}

export default ({
    pageSize = 10,
    isInModal = false,
    setIsSaveDisabled = undefined,
    productionRef = undefined,
    hiddenFields = undefined,
    disabledFields = undefined,
    showTypeFields = false
}: GuestContactInfoProps) => {
    const { theme } = useStyling();
    const { routeParams } = useHistory();
    const { entityId } = routeParams;
    const { validation, state, setState, handleChange } = useContext(FormContext);
    const { activeOrganizationAccount } = useContext(UserContext);
    const [contact, setContact] = useState<any>();
    const [updatedContact, setUpdatedContact] = useState(EMPTY_CONTACT);
    const isCreate = entityId === CONSTANTS_ID.createId;
    const [save, { loading: saveLoading }] = useMutationWithContext(autoCreateQuery, client);
    const [saveProduction, { loading: saveProductionLoading }] = useMutationWithContext(
        autoCreateProductionQuery,
        productionClient
    );
    const [findUserDetails, { loading, data }] = useLazyQuery(slimResultsQuery('Person'), {
        client: searchClient,
        fetchPolicy: FETCH_POLICY_CONFIG.apolloFetchPolicy,
        context: {
            headers: {
                ownerId: activeOrganizationAccount
            }
        }
    });

    useEffect(() => {
        if (data?.results?.hits?.items?.length > 0) {
            const { id } = data.results.hits.items[0];
            if (id !== state?.reference?.id) {
                const { id, name, organization, role, production, productionRole } = data.results.hits.items[0];
                setContact(data.results?.hits.items[0]);
                setState({ ...state, contactExists: true, entityId: id });
                setUpdatedContact({
                    Name: name,
                    Organization: organization,
                    Role: role,
                    Production: production,
                    ProductionRole: productionRole
                });
            }
        } else {
            setState({ ...state, contactExists: false });
        }
    }, [data]);

    const hasErrors = () => {
        const email = validation && validation?.errors?.contactInfo?.email;
        return email?.address && email.address.length > 0;
    };

    useEffect(() => {
        if (!hasErrors() && !isEmpty(state?.contactInfo?.email?.address)) {
            findUserDetails({
                variables: {
                    page: { from: 0, size: pageSize },
                    filters: [
                        { identifier: 'entity', value: 'Person' },
                        { identifier: 'email', value: state?.contactInfo?.email?.address }
                    ]
                }
            });
        }
    }, [state?.contactInfo?.email?.address]);

    const onClickUpdate = () => {
        const { name } = contact;
        const nameArray = name.split(' ');
        const newContact = {
            ...state,
            isUpdate: true,
            id: contact.id,
            name: {
                firstName: nameArray[0],
                lastName: nameArray[1]
            },
            contactInfo: contact.contactInfo,
            experiences: [
                {
                    experienceName: contact.organization || '',
                    professionalRoles: contact.role ? [contact.role] : []
                }
            ],
            creativeCredits: [
                {
                    showName: contact.production || '',
                    professionalRoles: contact.productionRole ? [contact.productionRole] : []
                }
            ]
        };
        setState(newContact);
    };

    useEffect(() => {
        handleChange('isOrganizationLoading', saveLoading);
    }, [handleChange, saveLoading]);

    useEffect(() => {
        handleChange('isProductionLoading', saveProductionLoading);
    }, [handleChange, saveProductionLoading]);

    useEffect(() => {
        setIsSaveDisabled?.(!validation?.isValid || saveLoading || state?.contactExists);
    }, [setIsSaveDisabled, validation, saveLoading, state?.contactExists]);

    return (
        <FormPanel>
            <FormContainer>
                <FormItem half={isInModal} quarter={!isInModal}>
                    <FormWidget name="name.firstName" label={<I18n token="guestEditor.label.firstName" />} />
                </FormItem>
                <FormItem half={isInModal} quarter={!isInModal}>
                    <FormWidget name="name.lastName" label={<I18n token="guestEditor.label.lastName" />} />
                </FormItem>
                {state?.contactExists && !state.isUpdate && (
                    <FormItem half={!isInModal}>
                        <Alert
                            severity="info"
                            sx={{ zIndex: '9999', width: '100%' }}
                            action={
                                isCreate && (
                                    <Button color="inherit" size="large" onClick={() => onClickUpdate()}>
                                        <I18n token="guestEditor.label.update" />
                                    </Button>
                                )
                            }
                        >
                            {updatedContact && (
                                <V>
                                    <Typography sx={{ fontWeight: 'bold' }}>
                                        <I18n token="guestEditor.emailValidation.matching" />
                                    </Typography>
                                    <Typography>
                                        <I18n token="guestEditor.emailValidation.sameEmail" />
                                    </Typography>
                                    {Object.keys(updatedContact).map((key, index) => (
                                        <H key={index}>
                                            <Typography color={theme.palette.text.disabled}>{`${key} : `}</Typography>{' '}
                                            <Typography>{` ${updatedContact[key]} `}</Typography>
                                        </H>
                                    ))}
                                </V>
                            )}
                        </Alert>
                    </FormItem>
                )}
                <FormItem half={!isInModal} hidden={hiddenFields?.contactInfo?.email}>
                    <FormWidget
                        name="contactInfo.email.address"
                        label={<I18n token="guestEditor.label.email" />}
                        hasErrors={hasErrors()}
                    />
                </FormItem>
                <FormItem half={!isInModal} hidden={disabledFields?.contactInfo?.email}>
                    <FormWidget
                        component="PhoneNumber"
                        name="contactInfo.phone.number"
                        label={<I18n token="guestEditor.label.phone" />}
                    />
                </FormItem>
                {showTypeFields && (
                    <>
                        <FormItem half={isInModal} quarter={!isInModal}>
                            <FormWidget
                                component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                                name="resourceType"
                                metadataName="ResourceType"
                                optionValues={[RESOURCE_TYPE.crew, RESOURCE_TYPE.cast, RESOURCE_TYPE.corporate]}
                                label={<I18n token="form.resource.type" />}
                            />
                        </FormItem>
                        <FormItem half={isInModal} quarter={!isInModal}>
                            <FormWidget
                                component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                                name="resourceSubType"
                                metadataName="CatalogSubType"
                                label={<I18n token="form.resource.subtype" />}
                            />
                        </FormItem>
                        <FormItem half={isInModal} quarter={!isInModal} hidden={disabledFields?.productionRole}>
                            <FormWidget
                                component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                                name="productionRole"
                                label={<I18n token="guestEditor.label.productionRole" />}
                                metadataName="Role"
                                allowCreate
                            />
                        </FormItem>
                        <FormItem
                            half={isInModal}
                            quarter={!isInModal}
                            hidden={disabledFields?.creativeCredits?.department}
                        >
                            <FormWidget
                                name="creativeCredits[0].department"
                                component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                                metadataName="Department"
                                label={<I18n token="guestEditor.label.productionDepartment" />}
                                allowCreate
                            />
                        </FormItem>
                    </>
                )}
                <FormItem half={!isInModal} hidden={disabledFields?.experiences?.organizationReference}>
                    <FormWidget
                        component="ReferenceAutocomplete"
                        name="experiences[0].organizationReference"
                        label={<I18n token="guestEditor.label.organization" />}
                        entity="Organization"
                        OptionRenderer={ParentOrganizationRenderer}
                        getOption={getOption}
                        addOption
                        onCreateHandle={quickAddOrganization}
                        saveMutation={save}
                        preload={false}
                        filter={{ expressions: [{ field: 'inactive', value: { values: ['false'] } }] }}
                    />
                </FormItem>
                <FormItem half={isInModal} quarter={!isInModal} hidden={disabledFields?.experiences?.title}>
                    <FormWidget
                        name="experiences[0].title"
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        metadataName="Title"
                        label={<I18n token="guestEditor.label.title" />}
                        allowCreate
                    />
                </FormItem>
                <FormItem half={isInModal} quarter={!isInModal} hidden={disabledFields?.experiences?.deparment}>
                    <FormWidget
                        name="experiences[0].department"
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        metadataName="Department"
                        label={<I18n token="guestEditor.label.department" />}
                        allowCreate
                    />
                </FormItem>
                <FormItem half={isInModal} quarter={!isInModal} hidden={disabledFields?.organizationRole}>
                    <FormWidget
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        name="organizationRole"
                        label={<I18n token="guestEditor.label.organizationRole" />}
                        metadataName="Role"
                        allowCreate
                        disabled={disabledFields?.organizationRole}
                    />
                </FormItem>
                <FormItem
                    half={isInModal}
                    quarter={!isInModal}
                    hidden={
                        hiddenFields?.creativeCredits?.showReference &&
                        !(productionRef && Object.keys(productionRef).length)
                    }
                >
                    <FormWidget
                        name="creativeCredits[0].showReference"
                        addOption
                        component="ReferenceAutocomplete"
                        entity="Production"
                        label={<I18n token="guestEditor.label.production" />}
                        OptionRenderer={ProductionOptionRenderer}
                        onCreateHandle={quickAddProduction}
                        getOption={getProductionOption}
                        saveMutation={saveProduction}
                    />
                </FormItem>
                <FormItem
                    half={isInModal}
                    quarter={!isInModal}
                    hidden={disabledFields?.productionRole || showTypeFields}
                >
                    <FormWidget
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        name="productionRole"
                        label={<I18n token="guestEditor.label.productionRole" />}
                        metadataName="Role"
                        allowCreate
                    />
                </FormItem>
                <FormItem
                    half={isInModal}
                    quarter={!isInModal}
                    hidden={disabledFields?.creativeCredits?.department || showTypeFields}
                >
                    <FormWidget
                        name="creativeCredits[0].department"
                        component={FORM_WIDGET_COMPONENTS.metadataAutocomplete}
                        metadataName="Department"
                        label={<I18n token="guestEditor.label.productionDepartment" />}
                        allowCreate
                    />
                </FormItem>
            </FormContainer>
        </FormPanel>
    );
};
